import { Button, Flex, Group, Loader, Menu, ScrollArea, Text } from '@mantine/core';
import { useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { EventsPanel } from './EventsPanel';
import { EventsHeader } from './EventsHeader';
import type { EventsFilterOption } from './EventsFilters';
import type { ActiveTaskItem } from './ActiveTasksList';
import { EventCard } from './EventCard';
import { EventsComposer } from './EventsComposer';

export type EventListItem = {
  id: string | number;
  content: ReactNode;
  meta?: ReactNode;
  isOwn?: boolean;
  actions?: ReactNode;
  actionsVisibility?: 'always' | 'hover';
};

interface EventsListProps {
  title?: string;
  events: EventListItem[];
  emptyText?: string;
  /** При true и пустом контенте в области списка показывается индикатор загрузки вместо emptyText */
  loading?: boolean;
  tasks?: ActiveTaskItem[];
  filters?: {
    options: EventsFilterOption[];
    active: string;
    onChange: (value: string) => void;
    sortOrder: 'asc' | 'desc';
    onSortChange: (order: 'asc' | 'desc') => void;
  };
  composer?: {
    /** Обработчик отправки текста composer'ом — родитель решает, куда сохранить (comments-rpc и т.п.) */
    onSubmit: (text: string) => Promise<void> | void;
    /** После отправки вызывается reload; можно вернуть Promise для ожидания обновления списка перед скроллом */
    onCreated?: () => void | Promise<void>;
    placeholder?: string;
  };
  taskAction?: {
    label?: string;
    onClick: () => void;
  };
}

/**
 * EventsList — унифицированный список событий с фильтрами и композером.
 */
export function EventsList({
  title,
  events,
  emptyText = 'Пока тут ничего нет',
  loading = false,
  tasks,
  filters,
  composer,
  taskAction,
}: EventsListProps) {
  const hasTasks = Boolean(tasks && tasks.length > 0);
  const hasEvents = events.length > 0;
  const hasContent = hasTasks || hasEvents;
  const showEmptyOrLoading = !hasContent;
  const showLoadingInList = showEmptyOrLoading && loading;

  const viewportRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = useCallback(() => {
    const el = viewportRef.current;
    if (el) el.scrollTop = el.scrollHeight - el.clientHeight;
  }, []);
  const handleComposerSubmit = useCallback(
    async (text: string) => {
      if (!composer) return;
      await composer.onSubmit(text);
      await Promise.resolve(composer.onCreated?.());
      setTimeout(scrollToBottom, 100);
    },
    [composer, scrollToBottom]
  );

  // При первой загрузке прокрутить в конец сообщений
  useEffect(() => {
    if (!loading && hasContent) {
      const t = setTimeout(scrollToBottom, 100);
      return () => clearTimeout(t);
    }
  }, [loading, hasContent, scrollToBottom]);

  return (
    <EventsPanel>
      <EventsHeader
        title={title}
        alignActions='left'
        actionsGrow
        actions={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {filters ? (
              <Group gap='xs'>
                <Menu position='bottom-start' withArrow withinPortal>
                  <Menu.Target>
                    <Text
                      size='xs'
                      c='white'
                      style={{
                        cursor: 'pointer',
                        borderBottom: '1px dotted rgba(255,255,255,0.8)',
                      }}
                    >
                      {filters.options.find(option => option.value === filters.active)?.label ||
                        'Все'}
                    </Text>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {filters.options.map(option => (
                      <Menu.Item key={option.value} onClick={() => filters.onChange(option.value)}>
                        {option.label}
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
              </Group>
            ) : (
              <span />
            )}
            {taskAction && (
              <Button
                size='compact-xs'
                radius='xl'
                variant='filled'
                styles={{ label: { fontSize: '0.7rem' }, root: { paddingInline: 8, height: 22 } }}
                onClick={taskAction.onClick}
              >
                {taskAction.label ?? 'Добавить задачу'}
              </Button>
            )}
          </div>
        }
      />

      <ScrollArea
        viewportRef={viewportRef}
        flex={1}
        mih={0}
        scrollbarSize={4}
        styles={{
          root: {
            minHeight: 180,
          },
          viewport: {
            paddingInlineStart: 8,
            paddingInlineEnd: 8,
            paddingBlockStart: 12,
            paddingBlockEnd: 0,
          },
          content: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
          },
          scrollbar: {
            background: 'transparent',
          },
          thumb: {
            background: 'rgba(0, 0, 0, 0.25)',
          },
        }}
      >
        {showEmptyOrLoading ? (
          <Flex align='center' justify='center' gap='xs' mih={180} flex={1} direction='column'>
            {showLoadingInList ? (
              <>
                <Loader size='sm' color='white' />
                <Text size='xs' c='white'>
                  Загрузка истории...
                </Text>
              </>
            ) : (
              <Text size='xs' c='white'>
                {emptyText}
              </Text>
            )}
          </Flex>
        ) : (
          <>
            {tasks && tasks.length > 0 && (
              <div
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  paddingBottom: 4,
                }}
              >
                {tasks.map(task => {
                  const parts: string[] = [];
                  if (task.assigneeLabel) parts.push(`Кому ${task.assigneeLabel}`);
                  if (task.dueLabel) parts.push(`до ${task.dueLabel}`);
                  const metaLine = parts.join(' ');

                  return (
                    <EventCard
                      key={`task-${task.id}`}
                      background='#fff9db'
                      onClick={task.onOpen}
                      onMouseEnter={task.onMouseEnter}
                      onMouseLeave={task.onMouseLeave}
                    >
                      <Group gap='xs' justify='space-between' wrap='nowrap'>
                        <Text size='xs'>{task.title}</Text>
                        {task.actions}
                      </Group>
                      {metaLine && (
                        <Text size='xs' c='dimmed' mt={2}>
                          {metaLine}
                        </Text>
                      )}
                    </EventCard>
                  );
                })}
              </div>
            )}
            {events.map(event => (
              <EventCard
                key={event.id}
                meta={event.meta}
                actions={event.actions}
                actionsVisibility={event.actionsVisibility}
                radius='md'
                background='#e5f8fa'
                style={{
                  width: 'fit-content',
                  maxWidth: '100%',
                  marginLeft: event.isOwn ? 'auto' : undefined,
                }}
              >
                {event.content}
              </EventCard>
            ))}
          </>
        )}
      </ScrollArea>

      {composer && (
        <EventsComposer onSubmit={handleComposerSubmit} placeholder={composer.placeholder} />
      )}
    </EventsPanel>
  );
}
